import React, { useEffect, useRef } from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';

// Founders keep their real portraits (order reversed vs. the original PPTX
// so the two swap positions per the user's earlier request).
const FOUNDER_PORTRAITS = ['image33.png', 'image32.png'];

// Sales / Regional leadership — same-slide contact block. Portraits stored
// under /public/team/*.jpg (JPEGs from the studio shoot).
const LEADERSHIP = [
  {
    roleLines: ['CSO', 'INTERNATIONAL'],
    name: 'Mario Steinrigl',
    photo: '/team/mario-steinrigl.png',
    email: 'm.steinrigl@stiggroup.com',
  },
  {
    roleLines: ['CEO', 'USA'],
    name: 'Mario Hoffmann',
    photo: '/team/mario-hoffmann.png',
    email: 'office.us@stig-international.com',
  },
  {
    roleLines: ['CEO', 'EUROPE'],
    name: 'Thomas Kutak',
    photo: '/team/thomas-kutak.jpg',
    email: 'office.eu@stig-international.com',
  },
  {
    roleLines: ['CEO', 'PHILIPPINES'],
    name: 'Elmar Terlutter',
    photo: '/team/elmar-terlutter.png',
    email: 'office.ph@stig-international.com',
  },
];

// Founders (order matches t.innovation.team): Stöger = CEO, Lang = COO.
const FOUNDER_ROLE_LINES = [
  ['CEO', 'INTERNATIONAL'],
  ['COO', 'INTERNATIONAL'],
];

export default function SectionInnovation() {
  const { t, content } = useT();
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    // Only start loading once the section is close to the viewport — huge
    // perf win: the ~15 MB office video no longer downloads on initial page load.
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!v.src && v.querySelector('source')) {
              v.load();
            }
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { rootMargin: '400px 0px', threshold: 0 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const founders = (t.innovation.team || []).slice(0, 2);
  const founderImgs = (content && content.innovation && content.innovation.images) || {};

  // Six leadership tiles rendered in a single compact 3×2 grid on the right.
  const roster = [
    ...founders.map((m, i) => ({
      type: 'founder',
      name: m.name,
      roleLines: FOUNDER_ROLE_LINES[i] || [m.role],
      portrait: founderImgs[`founder${i}`] || FOUNDER_PORTRAITS[i] || FOUNDER_PORTRAITS[0],
    })),
    ...LEADERSHIP.map((l) => ({
      type: 'leader',
      name: l.name,
      roleLines: l.roleLines,
      portrait: l.photo,
      photoPosition: l.photoPosition,
      photoScale: l.photoScale,
      email: l.email,
    })),
  ];

  return (
    <SectionShell id="innovation" className="relative overflow-hidden innovation-shell">
     <div className="innovation-layout">
      {/* Office reveal video — strictly clipped so nothing overlaying it can
          extend past its right edge (the previous card-overflow bug). */}
      <div
        className="innovation-video-frame"
        data-testid="innovation-office-backdrop"
      >
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          data-slide-persist="true"
          data-testid="innovation-office-video"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ filter: 'brightness(0.9) contrast(1.06) saturate(1.04)' }}
        >
          <source src="/videos/office.webm" type="video/webm" />
          <source src="/videos/office.mp4"  type="video/mp4" />
        </video>
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,12,16,0.35) 0%, transparent 20%, transparent 60%, rgba(10,12,16,0.85) 100%)',
          }}
        />
        <div className="absolute inset-0 rounded-2xl border border-white/[0.06] pointer-events-none" />

        {/* Copy card sits INSIDE the video frame so it can never overflow. */}
        <div className="innovation-copy-card">
          <Reveal>
            <div className="overline mb-4">{t.innovation.overline}</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-4 text-white">
              {t.innovation.title}
            </h2>
            <p className="font-ui text-sm md:text-base text-white/70 leading-relaxed mb-4">
              {t.innovation.body}
            </p>
            <address className="not-italic space-y-1" data-testid="innovation-hq">
              {(t.innovation.hqLines || [t.innovation.hq]).map((line, i) => (
                <div
                  key={i}
                  className={i === 0
                    ? 'font-display text-sm text-white/90 tracking-tight'
                    : 'overline text-white/50 text-[10px]'}
                >
                  {line}
                </div>
              ))}
            </address>
          </Reveal>
        </div>
      </div>

      {/* Right-hand roster: 6 leaders in a compact 3×2 grid, all on one screen. */}
      <div className="innovation-roster">
        <Reveal>
          <div className="overline mb-4 text-white/55">{t.innovation.leadershipTag || 'Leadership · Kontakt'}</div>
        </Reveal>
        <div className="innovation-roster-grid">
          {roster.map((p, i) => (
            <Reveal key={`${p.name}-${i}`} delay={80 + i * 60}>
              <figure
                className="innovation-person"
                data-testid={p.type === 'founder' ? `team-founder-${i}` : `team-leader-${i - 2}`}
              >
                <div className="innovation-person-avatar">
                  {p.portrait ? (
                    <img
                      src={p.type === 'founder' ? `/source-pptx/${p.portrait}` : p.portrait}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{
                        objectPosition: p.photoPosition || 'center top',
                        transform: p.photoScale ? `scale(${p.photoScale})` : undefined,
                        transformOrigin: p.photoScale ? 'center top' : 'center',
                        filter: 'grayscale(0.18) contrast(1.05) brightness(0.95)',
                      }}
                    />
                  ) : (
                    <span className="innovation-person-initials" aria-hidden="true">
                      {p.name.split(' ').map((s) => s[0]).slice(0, 2).join('')}
                    </span>
                  )}
                </div>
                <figcaption className="innovation-person-caption">
                  <div className="font-display text-white text-[17px] leading-tight tracking-tight">
                    {p.name}
                  </div>
                  <div className="overline text-white/45 mt-1 text-[11px]">
                    {(p.roleLines || []).map((ln, k) => (
                      <span key={k} className="block">{ln}</span>
                    ))}
                  </div>
                  {p.type === 'leader' && (
                    <div className="font-ui text-[12px] text-white/60 mt-2 leading-snug">
                      <a
                        href={`mailto:${p.email}`}
                        className="no-underline hover:text-[color:var(--stig-gold)] transition-colors"
                      >
                        <span className="block">{p.email.split('@')[0]}</span>
                        <span className="block">@{p.email.split('@')[1]}</span>
                      </a>
                    </div>
                  )}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
     </div>
    </SectionShell>
  );
}
